import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal, Marquee } from "../components/Reveal";
import { PageHero, CTABand, usePageTitle } from "../components/Shared";
import { IMAGES } from "../data/content";
import { useLang, useContent } from "../i18n";

const FORM_KEYS = ["Documents administratifs", "Création d'entreprise", "Services diaspora", "Services étrangers", "Services entreprises"];

const COPY = {
  fr: {
    meta: "Nos services",
    eyebrow: "Nos services",
    h1a: "Cinq pôles d'expertise,",
    h1b: "un seul interlocuteur.",
    sub: "Documents administratifs, création d'entreprise, accompagnement diaspora, étrangers et entreprises : DOC TOGO couvre l'ensemble de vos démarches au Togo.",
    cta: "Demander cette démarche",
    imgAlt: "Documents administratifs",
  },
  en: {
    meta: "Our services",
    eyebrow: "Our services",
    h1a: "Five areas of expertise,",
    h1b: "one single point of contact.",
    sub: "Administrative documents, company formation, diaspora support, foreign nationals and businesses: DOC TOGO covers all your procedures in Togo.",
    cta: "Request this procedure",
    imgAlt: "Administrative documents",
  },
};

export default function Services() {
  const { lang } = useLang();
  const { SERVICES, MARQUEE_ITEMS } = useContent();
  const c = COPY[lang];
  usePageTitle(c.meta);

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow={c.eyebrow}
        lines={[<>{c.h1a}</>, <>{c.h1b}</>]}
        sub={c.sub}
        image={IMAGES.documents}
        imageAlt={c.imgAlt}
      />
      <Marquee items={MARQUEE_ITEMS} dark />
      <section className="bg-ivory py-24 sm:py-28">
        <div className="mx-auto max-w-7xl space-y-24 px-6">
          {SERVICES.map((s, i) => (
            <div
              key={s.id}
              data-testid={`service-section-${s.id}`}
              className={`grid grid-cols-1 items-center gap-12 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <Reveal>
                <div className="relative">
                  <div className={`absolute -inset-3 rounded-[2rem] border border-gold/30 ${i % 2 === 1 ? "-rotate-2" : "rotate-2"}`} />
                  <img src={s.image} alt={s.title} loading="lazy" className="relative h-80 w-full rounded-[2rem] object-cover shadow-xl lg:h-[420px]" />
                  <span className="absolute -bottom-5 left-6 grid h-14 w-14 place-items-center rounded-2xl bg-brand text-white shadow-lg">
                    <s.icon className="h-7 w-7" />
                  </span>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">
                  {String(i + 1).padStart(2, "0")} — {s.title}
                </span>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 text-balance">
                  {s.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{s.short}</p>
                <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 rounded-xl border border-brand/10 bg-white px-4 py-3 text-sm font-medium text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/contact?categorie=${encodeURIComponent(FORM_KEYS[i])}`}
                  data-testid={`service-cta-${s.id}`}
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-display font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:-translate-y-0.5"
                >
                  {c.cta}
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          ))}
        </div>
      </section>
      <CTABand />
    </div>
  );
}
